import { useEffect, useState } from "react";
import {
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Drawer,
} from "@mui/material";

import { RichTreeView, SimpleTreeView } from "@mui/x-tree-view";
import { NetworkIcon } from "lucide-react";
import { useTranslation } from "react-i18next";

import { DynamicButton } from "components/DynamicButton/DynamicButton";
import { IZone } from "interfaces/zone.interface";
import { makeZoneTree } from "utils/zone.util";

import { useMonitoringStore } from "../monitoringStore";

type ZoneTreeProps = {
  zones: IZone[];
  currentSelection: string[];
  setCurrentSelection: (newSelection: string[]) => void;
};

function ZoneTree({
  zones,
  currentSelection,
  setCurrentSelection,
}: ZoneTreeProps) {
  return (
    <RichTreeView
      items={makeZoneTree(zones)}
      getItemLabel={({ name }) => name}
      defaultExpandedItems={
        zones[0] ? [zones[0]?.id, ...currentSelection] : currentSelection
      }
      multiSelect
      checkboxSelection
      selectedItems={currentSelection}
      onSelectedItemsChange={(_, newSelection) =>
        setCurrentSelection(newSelection)
      }
    />
  );
}

type ZoneSelectorProps = {
  zones: IZone[];
};

export function ZoneSelector({ zones }: ZoneSelectorProps) {
  const { t } = useTranslation();

  const [open, setOpen] = useState(false);
  const [currentSelection, setCurrentSelection] = useState<string[]>([]);

  const selectedZoneIds = useMonitoringStore((state) => state.selectedZoneIds);
  const setSelectedZoneIds = useMonitoringStore(
    (state) => state.setSelectedZoneIds,
  );

  useEffect(() => {
    setCurrentSelection(selectedZoneIds ?? []);
  }, [selectedZoneIds]);

  return (
    <>
      <DynamicButton
        startIcon={<NetworkIcon />}
        color={"info"}
        onClick={() => setOpen(true)}
      >
        {t("select_zones")}
      </DynamicButton>
      <Drawer open={open} onClose={() => setOpen(false)} anchor={"right"}>
        <DialogTitle>{t("select_zones")}</DialogTitle>
        <DialogContent dividers sx={{ width: "400px", maxWidth: "90vw" }}>
          <SimpleTreeView multiSelect checkboxSelection>
            <ZoneTree
              zones={zones}
              currentSelection={currentSelection}
              setCurrentSelection={setCurrentSelection}
            />
          </SimpleTreeView>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => {
              setSelectedZoneIds(currentSelection);
              setOpen(false);
            }}
          >
            {t("show_selected")}
          </Button>
        </DialogActions>
      </Drawer>
    </>
  );
}
